/**
 * Org context: the active org, this seat's role in it, and the workspaces
 * the account can open. OrgProvider (OrgProvider.tsx) resolves it from the
 * /o/:orgSlug URL against the user's memberships.
 *
 * No Firebase import here, deliberately. Screens and wrappers that only
 * read the context (RequireModule renders on the sign-in page's bundle)
 * would otherwise pull in Firestore with it.
 */
import { createContext, useContext, useMemo } from 'react';
import { addonStatus, addonsOn, isLegacyAddonShape, type AddonKey, type AddonStatus, type OrgDoc } from '@claripick/shared';

/** One workspace this account belongs to, as the switcher shows it. */
export interface Membership {
  orgId: string;
  name: string;
  slug: string;
  roleCode: string;
}

export interface OrgContextValue {
  orgId: string | null;
  orgSlug: string | null;
  org: (OrgDoc & { name: string }) | null;
  /** This user's role code in the active org ('a','e','f','d','r','b','v') or null. */
  roleCode: string | null;
  /**
   * Every workspace this account can open. Read from the `o` custom claim,
   * which already carries { orgId: roleCode } for all of them.
   *
   * There was no way to move between workspaces at all: RootRedirect takes
   * the first membership a `limit(1)` collection-group query returns and
   * the header prints one name. A treasurer, an auditor or a board member
   * who sits on three NPO boards — which the pricing page sells them a
   * login for — was pinned to whichever workspace that query happened to
   * hand back, with the URL as the only way out.
   */
  memberships: Membership[];
  loading: boolean;
}

export const OrgContext = createContext<OrgContextValue>({
  orgId: null,
  orgSlug: null,
  org: null,
  roleCode: null,
  memberships: [],
  loading: true,
});

export function useOrg(): OrgContextValue {
  return useContext(OrgContext);
}

/**
 * What this seat may do, mirroring firestore.rules exactly. Pages use
 * this to hide or disable what the rules would refuse, so a board
 * member never sees a "Score" button that quietly does nothing.
 */
export function useRole() {
  const { roleCode } = useOrg();
  const is = (codes: string[]) => roleCode !== null && codes.includes(roleCode);
  return {
    roleCode,
    known: roleCode !== null,
    isAdmin: is(['a']),
    canEdit: is(['a', 'e']),
    isFinance: is(['a', 'f']),
    isBoard: is(['a', 'b']),
    isReviewer: is(['a', 'r']),
    canCapture: is(['a', 'e', 'd']),
    isStaff: is(['a', 'e', 'f', 'd']),
  };
}

export function orgPath(orgId: string, ...segments: string[]): string {
  return ['orgs', orgId, ...segments].join('/');
}

/**
 * Which add-ons are on for this workspace (constants/addons.ts). Every menu,
 * the routes and the screens that link to an add-on read this, so a
 * screen never offers a door the menu does not show.
 *
 * Nothing is on until the org document has arrived: a flash of every
 * add-on on a slow connection would be a menu that shrinks under the thumb.
 */
export function useAddons(): {
  on: (key: AddonKey) => boolean;
  status: (key: AddonKey) => AddonStatus;
  list: AddonKey[];
  /** Opened before add-ons existed: keeps every screen its modules allow. */
  legacy: boolean;
} {
  const { org } = useOrg();
  return useMemo(() => {
    const list = addonsOn(org);
    return {
      on: (key: AddonKey) => list.includes(key),
      status: (key: AddonKey) => addonStatus(org, key),
      list,
      legacy: org ? isLegacyAddonShape(org) : false,
    };
  }, [org]);
}
